import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  Paper,
  Typography,
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from "@material-ui/core";
import {
  Smartphone,
  MoreVert,
  Settings,
  Link,
  CameraAlt
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  dialogPaper: {
    borderRadius: 16,
    background:
      theme.mode === "light"
        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        : "linear-gradient(135deg, #0f172a 0%, #111827 100%)",
  },
  contentPaper: {
    padding: theme.spacing(4),
    borderRadius: 12,
    textAlign: "center",
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.05)",
    boxShadow: theme.mode === "light" ? "0 10px 30px rgba(0,0,0,0.1)" : "0 10px 30px rgba(0,0,0,0.5)",
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    fontSize: "1.5rem",
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    fontSize: "1rem",
  },
  qrContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    margin: theme.spacing(2, 0),
    border: `1px solid ${theme.palette.divider}`,
    display: "inline-block",
  },
  qrCode: {
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  instructions: {
    marginTop: theme.spacing(3),
    textAlign: "left",
  },
  instructionList: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  listItem: {
    padding: theme.spacing(1, 0),
  },
  listIcon: {
    minWidth: 40,
    color: theme.palette.primary.main, // <-- cor seguindo o whitelabel
  },
  timer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: 20,
    display: "inline-block",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(4),
  },
  brand: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: "1.2rem",
    marginBottom: theme.spacing(1),
  },
}));

const QrcodeModal = ({ open, onClose, whatsAppId, onConnected }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [qrCode, setQrCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(60);
  const [refreshingQr, setRefreshingQr] = useState(false);
  const requestedOnOpenRef = useRef(false);
  const requestingQrRef = useRef(false);
  const openedAtRef = useRef(0);
  const { user, socket } = useContext(AuthContext);

  const requestNewQrCode = useCallback(async () => {
    if (!whatsAppId) return;
    if (requestingQrRef.current) return;

    try {
      requestingQrRef.current = true;
      setRefreshingQr(true);
      setQrCode("");
      setTimeLeft(60);
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    } finally {
      requestingQrRef.current = false;
      setRefreshingQr(false);
    }
  }, [whatsAppId]);

  // Ao abrir, sempre força QR NOVO.
  useEffect(() => {
    if (!open) {
      requestedOnOpenRef.current = false;
      setQrCode("");
      setRefreshingQr(false);
      setTimeLeft(60);
      return;
    }

    if (!whatsAppId) return;

    if (!requestedOnOpenRef.current) {
      requestedOnOpenRef.current = true;
      openedAtRef.current = Date.now();
      requestNewQrCode();
    }
  }, [open, whatsAppId, onClose, requestNewQrCode]);

  // Fallback de sincronização por polling.
  // Garante atualização do QR/status mesmo quando o evento de socket falhar.
  useEffect(() => {
    if (!open || !whatsAppId) return;

    let isMounted = true;

    const pollSession = async () => {
      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`, {
          params: { _ts: Date.now() }
        });
        if (!isMounted) return;

        if (data?.status === "CONNECTED") {
          setRefreshingQr(false);
          if (typeof onConnected === "function") {
            onConnected();
          }
          onClose();
          return;
        }

        const updatedAtMs = data?.updatedAt ? new Date(data.updatedAt).getTime() : 0;
        const isFreshQr = updatedAtMs >= openedAtRef.current;

        if (data?.qrcode && data.qrcode !== qrCode && isFreshQr) {
          setQrCode(data.qrcode);
          setTimeLeft(60);
          setRefreshingQr(false);
        }

        if (data?.status === "qrcode" && data?.qrcode && isFreshQr) {
          setRefreshingQr(false);
        }
      } catch {
        // evita ruído de toast durante polling contínuo
      }
    };

    pollSession();
    const interval = setInterval(pollSession, 3000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [open, whatsAppId, onClose, onConnected, qrCode]);

  useEffect(() => {
    if (!open) return;
    if (!qrCode) return;

    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          // Ao expirar, pedir um QR novo de verdade (não apenas resetar o contador)
          requestNewQrCode();
          return 60;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, qrCode, requestNewQrCode]);

  useEffect(() => {
    if (!whatsAppId) return;
    if (!socket || typeof socket.on !== "function" || typeof socket.off !== "function") return;
    const companyId = user.companyId;

    const onWhatsappData = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        const updatedAtMs = data?.session?.updatedAt
          ? new Date(data.session.updatedAt).getTime()
          : 0;
        const isFreshQr = updatedAtMs >= openedAtRef.current;

        if (data?.session?.qrcode && isFreshQr) {
          setQrCode(data.session.qrcode);
          setTimeLeft(60);
          setRefreshingQr(false);
        }

        // Fecha o modal apenas quando conectar de fato.
        // Durante regeneração, o backend zera qrcode enquanto está em OPENING/DISCONNECTED.
        if (data.session.status === "CONNECTED") {
          if (typeof onConnected === "function") {
            onConnected();
          }
          onClose();
        }
      }
    };

    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    };
  }, [whatsAppId, onClose, onConnected, user.companyId, socket]);

  const instructions = [
    {
      icon: <Smartphone />,
      text: "Abra o WhatsApp no seu celular",
    },
    {
      icon: <MoreVert />,
      text: "Toque em Mais opções no Android ou em Configurações no iPhone",
    },
    {
      icon: <Link />,
      text: "Toque em Dispositivos conectados e depois em Conectar dispositivos",
    },
    {
      icon: <CameraAlt />,
      text:
        "Aponte a câmera do celular para esta tela para escanear o QR Code",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogContent style={{ padding: 0 }}>
        <Paper className={classes.contentPaper}>
          <div className={classes.root}>
            <Typography className={classes.brand}>
              {/* você pode colocar o nome do sistema aqui, se quiser */}
            </Typography>
            <Typography className={classes.title}>
              Conectar WhatsApp
            </Typography>
            <Typography className={classes.subtitle}>
              Escaneie o QR Code para vincular sua conta do WhatsApp
            </Typography>

            {qrCode ? (
              <>
                <div className={classes.qrContainer}>
                  <QRCode
                    value={qrCode}
                    size={280}
                    className={classes.qrCode}
                    fgColor={theme.mode === "dark" ? "#e5e7eb" : "#1f2937"}
                    bgColor={theme.palette.background.paper}
                    level="H"
                  />
                </div>

                <div className={classes.timer}>Atualiza em: {timeLeft}s</div>

                <div className={classes.instructions}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Como conectar:
                  </Typography>
                  <List className={classes.instructionList} dense>
                    {instructions.map((instruction, index) => (
                      <ListItem key={index} className={classes.listItem}>
                        <ListItemIcon className={classes.listIcon}>
                          {instruction.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={instruction.text}
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </div>

                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginTop: 16 }}
                >
                  O QR Code atualiza automaticamente a cada 60 segundos
                </Typography>
              </>
            ) : (
              <div className={classes.loadingContainer}>
                <CircularProgress
                  size={40}
                  style={{ color: "#667eea" }}
                />
                <Typography variant="body1" color="textSecondary">
                  Aguardando pelo QR Code...
                </Typography>
              </div>
            )}
          </div>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
